/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Itinerary, ItineraryItem } from '../types';
import {
  getItinerary, getInvitedItinerary,
  publishItinerary,
  updateItineraryItem,
  deleteItineraryItem,
  reorderItineraryItems,
  duplicateItinerary,
  getItineraryShareInfo,
  deleteItinerary,
  sendItineraryChatMessage,
  getItineraryChatHistory,
  ChatMessage,
} from '../services';
import { createItineraryItem } from '../services';
import { getChatModels, ChatModelOption } from '../services/chatApi';
import { useEventsStore } from '../stores';

export type ViewTab = 'timeline' | 'map' | 'chat';

export interface EditingItem {
  id: number;
  field: 'name' | 'time' | 'location' | 'notes' | 'duration' | 'type';
  value: string;
}

export interface PendingChanges {
  changes: { action: string; name: string }[];
  suggestions: string[];
  response: string;
}

export interface GroupedItems {
  [date: string]: ItineraryItem[];
}

export const typeOptions = [
  { value: 'activity', label: 'Activity' },
  { value: 'meal', label: 'Meal' },
  { value: 'travel', label: 'Travel' },
  { value: 'depart', label: 'Depart' },
  { value: 'arrive', label: 'Arrive' },
  { value: 'checkin', label: 'Check In' },
  { value: 'checkout', label: 'Check Out' },
  { value: 'break', label: 'Break' },
  { value: 'other', label: 'Other' },
];


export function useItineraryPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [error, setErrorRaw] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setError = useCallback((msg: string, autoClearMs = 5000) => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setErrorRaw(msg);
    if (msg && autoClearMs > 0) {
      errorTimerRef.current = setTimeout(() => setErrorRaw(''), autoClearMs);
    }
  }, []);

  const clearError = useCallback(() => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setErrorRaw('');
  }, []);
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null);
  const [shareInfo, setShareInfo] = useState<{ share_token: string; share_url: string } | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<PendingChanges | null>(null);
  const [activeTab, setActiveTab] = useState<ViewTab>('timeline');
  const [chatInput, setChatInput] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [modelOptions, setModelOptions] = useState<ChatModelOption[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());
  const [hasInitializedCollapse, setHasInitializedCollapse] = useState<number | null>(null);

  useEffect(() => {
    const intervalId = setInterval(() => setCurrentTime(new Date()), 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Fetch available chat models
  useEffect(() => {
    getChatModels().then(({ models, default: defaultModel }) => {
      setModelOptions(models);
      setSelectedModel(prev => prev || defaultModel);
    }).catch(() => {
      setSelectedModel(prev => prev || 'gpt-4o');
    });
  }, []);

  const getEventStatus = useCallback((item: ItineraryItem) => {
    if (!item.date || !item.time) return 'upcoming';

    const eventStart = new Date(`${item.date}T${item.time}:00`);
    let eventEnd = eventStart;

    if (item.duration) {
      const durationParts = item.duration.match(/(\d+)\s*(hour|minute)s?/i);
      if (durationParts) {
        const value = parseInt(durationParts[1]);
        const unit = durationParts[2].toLowerCase();
        eventEnd = new Date(eventStart);
        if (unit.startsWith('hour')) {
          eventEnd.setHours(eventEnd.getHours() + value);
        } else if (unit.startsWith('minute')) {
          eventEnd.setMinutes(eventEnd.getMinutes() + value);
        }
      }
    } else {
      const itemWithEndTime = item as ItineraryItem & { end_time?: string | null };
      if (itemWithEndTime.end_time) {
        eventEnd = new Date(`${item.date}T${itemWithEndTime.end_time}:00`);
      }
    }

    if (currentTime > eventEnd) return 'past';
    if (currentTime >= eventStart && currentTime <= eventEnd) return 'current';
    return 'upcoming';
  }, [currentTime]);

  // Returns whether the plan on screen is now the server's version. Callers
  // that go on to TELL the user "it has been reloaded" must not say so on a
  // failed load: the claim would be false and the stale revision would wedge
  // every retry behind the same conflict.
  const loadItinerary = useCallback(async (itineraryId: number): Promise<boolean> => {
    try {
      // Check for invite token in URL — use public endpoint if present
      const params = new URLSearchParams(window.location.search);
      const inviteToken = params.get('invite');
      const data = inviteToken
        ? await getInvitedItinerary(inviteToken)
        : await getItinerary(itineraryId);
      setItinerary(data);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load itinerary');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadChatHistory = useCallback(async (itineraryId: number) => {
    try {
      const chatData = await getItineraryChatHistory(itineraryId);
      setChatMessages(chatData.conversation_history || []);
    } catch (err) {
      console.error('Failed to load chat history:', err);
    }
  }, []);

  useEffect(() => {
    if (id) {
      const itineraryId = parseInt(id, 10);
      loadItinerary(itineraryId);
      loadChatHistory(itineraryId);
    }
  }, [id]);

  // Scroll to bottom of chat when new messages arrive or chat tab opens
  useEffect(() => {
    if (activeTab === 'chat' && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeTab]);

  const invalidateEvents = useEventsStore((s) => s.invalidate);

  const handlePublish = useCallback(async (shouldPublish: boolean) => {
    if (!itinerary) return;
    const originalItinerary = itinerary;
    setItinerary(prev => prev ? { ...prev, is_published: shouldPublish } : null);

    try {
      const result = await publishItinerary(itinerary.id, shouldPublish);
      setItinerary(result.itinerary);
      setSuccessMessage(result.message);
      setTimeout(() => setSuccessMessage(''), 3000);
      invalidateEvents();
    } catch (err) {
      setItinerary(originalItinerary);
      setError(err instanceof Error ? err.message : 'Failed to update publish status');
    }
  }, [itinerary, invalidateEvents]);

  const handleEnterEditMode = useCallback(async () => {
    if (!itinerary) return;
    if (itinerary.is_published) {
      try {
        await publishItinerary(itinerary.id, false);
        setItinerary(prev => prev ? { ...prev, is_published: false } : null);
        setSuccessMessage('Removed from calendar for editing. Publish when done.');
        setTimeout(() => setSuccessMessage(''), 5000);
        invalidateEvents();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to unpublish');
        return;
      }
    }
    setIsEditMode(true);
  }, [itinerary]);

  const formatDate = useCallback((dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
  }, []);

  const formatShortDate = useCallback((dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long', month: 'short', day: 'numeric',
    });
  }, []);

  const formatDateRange = useCallback((startDate: string | null, endDate: string | null) => {
    if (!startDate) return 'No dates set';
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

    if (startDate === endDate) {
      return start.toLocaleDateString('en-US', { ...options, year: 'numeric' });
    }
    if (start.getFullYear() === end.getFullYear()) {
      if (start.getMonth() === end.getMonth()) {
        return `${start.toLocaleDateString('en-US', options)}-${end.getDate()}, ${start.getFullYear()}`;
      }
      return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}, ${start.getFullYear()}`;
    }
    return `${start.toLocaleDateString('en-US', { ...options, year: 'numeric' })} - ${end.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
  }, []);

  const formatTime = useCallback((timeStr: string | null) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  }, []);

  const groupItemsByDate = useCallback(() => {
    if (!itinerary) return {};
    const grouped: GroupedItems = {};

    itinerary.items.forEach(item => {
      const date = item.date || itinerary.start_date || 'unscheduled';
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(item);
    });

    const sortedGrouped: GroupedItems = {};
    Object.keys(grouped).sort().forEach(date => {
      sortedGrouped[date] = grouped[date];
    });
    return sortedGrouped;
  }, [itinerary]);

  // Start all days collapsed per prototype — expand on tap
  useEffect(() => {
    if (!itinerary || hasInitializedCollapse === itinerary.id) return;
    const grouped = groupItemsByDate();
    setCollapsedDays(new Set(Object.keys(grouped)));
    setHasInitializedCollapse(itinerary.id);
  }, [itinerary?.id]);

  const toggleDayCollapsed = useCallback((date: string) => {
    setCollapsedDays(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  }, []);

  const collapseAllDays = useCallback(() => {
    const grouped = groupItemsByDate();
    setCollapsedDays(new Set(Object.keys(grouped)));
  }, [groupItemsByDate]);

  const expandAllDays = useCallback(() => setCollapsedDays(new Set()), []);

  const handleStartEdit = useCallback((itemId: number, field: EditingItem['field'], currentValue: string) => {
    if (!isEditMode) return;
    setEditingItem({ id: itemId, field, value: currentValue || '' });
  }, [isEditMode]);

  const handleEditChange = useCallback((value: string) => {
    if (editingItem) {
      setEditingItem({ ...editingItem, value });
    }
  }, [editingItem]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingItem || !itinerary) return;
    try {
      await updateItineraryItem(itinerary.id, editingItem.id, {
        [editingItem.field]: editingItem.value,
      }, itinerary.content_revision ?? 0);
      await loadItinerary(itinerary.id);
      setEditingItem(null);
      setSuccessMessage('Item updated');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch {
      setError('Failed to update item');
    }
  }, [editingItem, itinerary, loadItinerary]);

  const handleCancelEdit = useCallback(() => {
    setEditingItem(null);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSaveEdit();
    else if (e.key === 'Escape') handleCancelEdit();
  }, [handleSaveEdit, handleCancelEdit]);

  const handleAddItem = useCallback(async (date: string) => {
    if (!itinerary) return;
    try {
      await createItineraryItem(itinerary.id, {
        name: 'New Activity', date, type: 'activity',
      }, itinerary.content_revision ?? 0);
      await loadItinerary(itinerary.id);
      setSuccessMessage('Activity added');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add item';
      setError(errorMessage);
    }
  }, [itinerary, loadItinerary]);

  const handleDeleteItem = useCallback((itemId: number) => {
    setDeleteItemId(itemId);
  }, []);

  const confirmDeleteItem = useCallback(async () => {
    if (!itinerary || deleteItemId === null) return;
    const itemId = deleteItemId;
    try {
      await deleteItineraryItem(itinerary.id, itemId, itinerary.content_revision ?? 0);
    } catch {
      // DELETE may 504 via Cloudflare even when successful — verify by re-fetching
    }
    // Always re-fetch to check actual state
    try {
      const fresh = await getItinerary(itinerary.id);
      setItinerary(fresh);
      const itemGone = !fresh.items.some((i: { id: number }) => i.id === itemId);
      if (itemGone) {
        setSuccessMessage('Activity deleted');
        setTimeout(() => setSuccessMessage(''), 2000);
      } else {
        setError('Failed to delete item');
      }
    } catch {
      setError('Failed to verify deletion');
    }
    setDeleteItemId(null);
  }, [itinerary, deleteItemId]);

  const cancelDeleteItem = useCallback(() => {
    setDeleteItemId(null);
  }, []);

  const handleDragStart = useCallback((itemId: number) => {
    setDraggedItem(itemId);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(async (targetItemId: number, date: string) => {
    if (!draggedItem || !itinerary || draggedItem === targetItemId) {
      setDraggedItem(null);
      return;
    }
    const grouped = groupItemsByDate();
    const dateItems = grouped[date] || [];
    const draggedIndex = dateItems.findIndex((item: ItineraryItem) => item.id === draggedItem);
    const targetIndex = dateItems.findIndex((item: ItineraryItem) => item.id === targetItemId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      return;
    }

    const newOrder = [...dateItems];
    const [removed] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, removed);

    try {
      await reorderItineraryItems(
        itinerary.id,
        newOrder.map((item: ItineraryItem) => item.id),
        itinerary.content_revision ?? 0,
      );
      await loadItinerary(itinerary.id);
    } catch {
      setError('Failed to reorder items');
    }
    setDraggedItem(null);
  }, [draggedItem, itinerary, groupItemsByDate, loadItinerary]);

  const handleDuplicate = useCallback(async () => {
    if (!itinerary) return;
    try {
      const result = await duplicateItinerary(itinerary.id);
      setSuccessMessage('Itinerary duplicated! Redirecting...');
      setTimeout(() => navigate(`/itinerary/${result.itinerary.id}`), 1000);
    } catch {
      setError('Failed to duplicate itinerary');
    }
  }, [itinerary, navigate]);

  const handleShare = useCallback(async () => {
    if (!itinerary) return;
    try {
      const info = await getItineraryShareInfo(itinerary.id);
      setShareInfo(info);
      setShowShareModal(true);
    } catch {
      setError('Failed to get share info');
    }
  }, [itinerary]);

  const copyShareLink = useCallback(() => {
    if (!shareInfo) return;
    const fullUrl = shareInfo.share_url.startsWith('http')
      ? shareInfo.share_url
      : `${window.location.origin}${shareInfo.share_url}`;
    navigator.clipboard.writeText(fullUrl);
    setSuccessMessage('Link copied to clipboard!');
    setTimeout(() => setSuccessMessage(''), 2000);
  }, [shareInfo]);

  const hadChatError = useRef(false);
  const MAX_RETRIES = 3;

  const handleSendChatMessage = useCallback(async (message: string, retryCount = 0) => {
    if (!message.trim() || !itinerary) return;
    setIsChatLoading(true);

    // On first send, add user bubble; on retry, remove the error bubble
    if (retryCount === 0) {
      setChatMessages(prev => [...prev, { role: 'user', content: message }]);
    } else {
      setChatMessages(prev => prev.filter(m => !m.isError));
    }

    let response;
    try {
      response = await sendItineraryChatMessage(message, itinerary.id, selectedModel || undefined);
    } catch {
      hadChatError.current = true;
      const isFinal = retryCount >= MAX_RETRIES - 1;
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: isFinal
          ? "Still having trouble. Please try again later."
          : "Something went wrong.",
        isError: true,
        originalMessage: isFinal ? undefined : message,
        retryCount,
      }]);
      setIsChatLoading(false);
      return;
    }

    try {
      if (response.conversation_history) {
        setChatMessages(response.conversation_history);
      }

      // Clear any lingering error and show recovery toast
      clearError();
      if (hadChatError.current) {
        hadChatError.current = false;
        setSuccessMessage('Itinerary updated!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }

      if (response.changes && response.changes.length > 0) {
        await loadItinerary(itinerary.id);
        setPendingChanges({
          changes: response.changes,
          suggestions: response.suggestions || [],
          response: response.response,
        });
      } else {
        await loadItinerary(itinerary.id);
      }
    } catch (err) {
      console.error('Error applying chat response to UI:', err);
    } finally {
      setIsChatLoading(false);
    }
  }, [itinerary, loadItinerary, clearError, selectedModel]);

  const handleRetryChat = useCallback((originalMessage: string, retryCount: number) => {
    handleSendChatMessage(originalMessage, retryCount + 1);
  }, [handleSendChatMessage]);

  const handleApplyChanges = useCallback(async () => {
    if (!pendingChanges) return;
    setSuccessMessage(`Kept ${pendingChanges.changes.length} change${pendingChanges.changes.length > 1 ? 's' : ''}`);
    setTimeout(() => setSuccessMessage(''), 3000);
    setPendingChanges(null);
  }, [pendingChanges]);

  const handleDiscardChanges = useCallback(async () => {
    if (!itinerary) return;
    try {
      await fetch(`/api/planner/itineraries/${itinerary.id}/undo/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      await loadItinerary(itinerary.id);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Changes discarded. What would you like to do instead?',
      }]);
    } catch {
      setError('Failed to undo changes');
    }
    setPendingChanges(null);
  }, [itinerary, loadItinerary]);

  const handleExportPDF = useCallback(async () => {
    if (!printRef.current || !itinerary) return;
    setIsExporting(true);

    // Save collapsed state and expand all for print
    const savedCollapsed = new Set(collapsedDays);
    setCollapsedDays(new Set());

    // Wait for React to re-render expanded days
    await new Promise(r => setTimeout(r, 300));

    const element = printRef.current;
    element.classList.add('pdf-exporting');

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      element.classList.remove('pdf-exporting');
      setCollapsedDays(savedCollapsed);
      setIsExporting(false);
      window.removeEventListener('afterprint', cleanup);
    };

    try {
      window.addEventListener('afterprint', cleanup);
      // Safety timeout — afterprint can be unreliable in some browsers
      setTimeout(cleanup, 10000);
      window.print();
    } catch {
      cleanup();
    }
  }, [itinerary, collapsedDays]);

  const handleDeleteItinerary = useCallback(async (deleteEvents: boolean) => {
    if (!itinerary) return;
    setIsDeleting(true);
    setShowDeleteDialog(false);
    setError('');

    try {
      const result = await deleteItinerary(itinerary.id, deleteEvents);
      const eventsDeleted = result.events_deleted ?? 0;
      const eventsMessage = eventsDeleted > 0
        ? ` and ${eventsDeleted} calendar event${eventsDeleted > 1 ? 's' : ''}`
        : '';
      setSuccessMessage(`Itinerary deleted${eventsMessage}. Redirecting...`);
      setTimeout(() => navigate('/plans'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete itinerary');
      setIsDeleting(false);
    }
  }, [itinerary, navigate]);

  const handleChatSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (chatInput.trim() && !isChatLoading) {
      const message = chatInput.trim();
      setChatInput('');
      handleSendChatMessage(message);
    }
  }, [chatInput, isChatLoading, handleSendChatMessage]);

  const toggleNoteExpanded = useCallback((itemId: number) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) { newSet.delete(itemId); } else { newSet.add(itemId); }
      return newSet;
    });
  }, []);

  return {
    // Refs
    printRef,
    chatEndRef,

    // Navigation
    navigate,

    // State
    itinerary,
    setItinerary,
    isLoading,
    error,
    setError,
    clearError,
    successMessage,
    activeTab,
    setActiveTab,
    isEditMode,
    setIsEditMode,
    editingItem,
    draggedItem,
    expandedNotes,
    collapsedDays,
    showEditModal,
    setShowEditModal,
    showShareModal,
    setShowShareModal,
    shareInfo,
    showDeleteDialog,
    setShowDeleteDialog,
    isDeleting,
    showMoreMenu,
    setShowMoreMenu,
    isExporting,

    // Chat
    chatMessages,
    chatInput,
    setChatInput,
    isChatLoading,
    pendingChanges,
    selectedModel,
    setSelectedModel,
    modelOptions,

    // Formatters
    formatDate,
    formatShortDate,
    formatDateRange,
    formatTime,
    groupItemsByDate,
    getEventStatus,

    // Handlers
    handlePublish,
    handleEnterEditMode,
    handleStartEdit,
    handleEditChange,
    handleSaveEdit,
    handleCancelEdit,
    handleKeyDown,
    handleAddItem,
    handleDeleteItem,
    deleteItemId,
    confirmDeleteItem,
    cancelDeleteItem,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDuplicate,
    handleShare,
    copyShareLink,
    handleSendChatMessage,
    handleRetryChat,
    handleApplyChanges,
    handleDiscardChanges,
    handleExportPDF,
    handleDeleteItinerary,
    handleChatSubmit,
    toggleNoteExpanded,
    toggleDayCollapsed,
    collapseAllDays,
    expandAllDays,
    loadItinerary,
  };
}

export type UseItineraryPageReturn = ReturnType<typeof useItineraryPage>;
